import React, { useState, useEffect, useRef } from 'react';
import { klineMonitorService } from '../../services/klineMonitorService';
import { RankingAlgorithm, BarInterval } from '@/services/enums';
import { watchlistSelfService } from '../../services/watchlistSelfService';
import { SystemPromptInfo } from '../../services/adminService';
import MultiSelectDropdown from '../MultiSelectDropdown';
import SelectDropdown from '../SelectDropdown';
import { useToastStore } from '../../stores/useToastStore';
import { useKlineMonitorStore } from '../../stores/useKlineMonitorStore';
import { COMMON_TIMEFRAMES } from '../../constants/timeframes';

interface AutoMonitorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: {
    enableAutoTrading: boolean;
    monitorCount: number;
    minVolume: number;
    excludedSymbols: string[];
    systemPromptId?: string;
    tradingParams: {
      leverage: number;
      positionSize: number;
      takeProfitPercent: number;
      stopLossPercent: number;
    };
    rankingAlgorithm: RankingAlgorithm;
    timeFrame: BarInterval;
  }) => Promise<void>;
  loading: boolean;
  systemPrompts: SystemPromptInfo[];
  loadingPrompts: boolean;
}

const AutoMonitorDialog: React.FC<AutoMonitorDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  systemPrompts,
  loadingPrompts,
}) => {
  const [monitorCount, setMonitorCount] = useState<number | ''>(5);
  const [minVolume, setMinVolume] = useState<number | ''>(10000000);
  const [rankingAlgorithm, setRankingAlgorithm] = useState<RankingAlgorithm>('volatility');
  const [timeFrame, setTimeFrame] = useState<BarInterval>('1H');
  const [enableAutoTrading, setEnableAutoTrading] = useState(false);
  const [leverage, setLeverage] = useState<number | ''>(3);
  const [positionSize, setPositionSize] = useState<number | ''>(1000);
  const [takeProfitPercent, setTakeProfitPercent] = useState<number | ''>(20);
  const [stopLossPercent, setStopLossPercent] = useState<number | ''>(10);
  const [excludedSymbols, setExcludedSymbols] = useState<string[]>([]);
  const [selectedSystemPromptId, setSelectedSystemPromptId] = useState<string | undefined>(undefined);

  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [previewSymbols, setPreviewSymbols] = useState<Array<{ symbol: string; value: number }>>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  const { getAutoMonitorConfig } = useKlineMonitorStore();

  const configLoadedRef = useRef(false);
  const loadingSymbolsRef = useRef(false);
  const showToast = useToastStore((state) => state.show);

  // 处理数值输入变更
  const handleNumberInput = (
    value: string,
    setter: (val: number | '') => void,
    max?: number,
    isFloat: boolean = false,
  ) => {
    if (value === '') {
      setter('');
      return;
    }
    const num = isFloat ? parseFloat(value) : parseInt(value);
    if (isNaN(num)) return;

    if (max !== undefined && num > max) {
      setter(max);
    } else {
      setter(num);
    }
  };

  // 加载币种列表
  useEffect(() => {
    const loadSymbols = async () => {
      if (availableSymbols.length > 0 || loadingSymbolsRef.current) return;

      try {
        loadingSymbolsRef.current = true;
        setLoadingSymbols(true);
        const symbols = await watchlistSelfService.getDistinctSymbols();
        setAvailableSymbols(symbols.sort());
      } catch (error: any) {
        console.error('获取币种列表失败:', error);
        showToast(error.message || '获取币种列表失败', 'error');
      } finally {
        setLoadingSymbols(false);
        loadingSymbolsRef.current = false;
      }
    };

    if (isOpen) {
      loadSymbols();
    }
  }, [isOpen, availableSymbols.length, showToast]);

  // 加载初始配置
  useEffect(() => {
    if (!isOpen) {
      configLoadedRef.current = false;
      setConfigReady(false);
      return;
    }

    if (configLoadedRef.current) return;
    configLoadedRef.current = true;

    const loadConfig = async () => {
      try {
        const config = await getAutoMonitorConfig();
        if (config) {
          setMonitorCount(config.monitorCount || 5);
          setMinVolume(config.minVolume || 10000000);
          setRankingAlgorithm(config.rankingAlgorithm || 'volatility');
          setTimeFrame(config.timeFrame || '1H');
          setEnableAutoTrading(config.enableAutoTrading || false);
          setExcludedSymbols(config.excludedSymbols || []);
          if (config.systemPromptId) setSelectedSystemPromptId(config.systemPromptId);

          if (config.tradingParams) {
            setLeverage(config.tradingParams.leverage || 3);
            setPositionSize(config.tradingParams.positionSize || 1000);
            setTakeProfitPercent((config.tradingParams.takeProfitPercent || 0.2) * 100);
            setStopLossPercent((config.tradingParams.stopLossPercent || 0.1) * 100);
          }
        }
      } catch (error) {
        console.error('加载自主监控配置失败:', error);
      } finally {
        setConfigReady(true);
      }
    };

    loadConfig();
  }, [isOpen, getAutoMonitorConfig]);

  // 加载预览币种
  useEffect(() => {
    if (!isOpen || !configReady) return;

    // 验证参数有效性
    const count = Number(monitorCount);
    if (monitorCount === '' || isNaN(count) || count < 1 || count > 50) {
      return;
    }

    const vol = Number(minVolume);
    if (minVolume === '' || isNaN(vol) || vol < 0) {
      return;
    }

    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const symbols = await klineMonitorService.previewAutoMonitorSymbols(
          Number(monitorCount) || 5,
          excludedSymbols,
          Number(minVolume) || 0,
          rankingAlgorithm,
          timeFrame,
        );
        setPreviewSymbols(symbols);
      } catch (error) {
        console.error('加载预览币种失败:', error);
      } finally {
        setLoadingPreview(false);
      }
    };

    const timer = setTimeout(loadPreview, 500);
    return () => clearTimeout(timer);
  }, [isOpen, monitorCount, excludedSymbols, minVolume, rankingAlgorithm, timeFrame, configReady]);

  const handleConfirm = () => {
    // 验证 monitorCount
    const count = Number(monitorCount);
    if (!monitorCount || isNaN(count) || count < 1 || count > 50) {
      showToast('启动数量必须在 1 到 50 之间', 'error');
      return;
    }

    // 验证 minVolume
    const vol = Number(minVolume);
    if (minVolume === '' || isNaN(vol) || vol < 0) {
      showToast('最低成交量无效', 'error');
      return;
    }

    // 验证 leverage
    const lev = Number(leverage);
    if (!leverage || isNaN(lev) || lev < 1 || lev > 5) {
      showToast('杠杆倍数必须在 1 到 5 之间', 'error');
      return;
    }

    // 验证 positionSize
    const size = Number(positionSize);
    if (!positionSize || isNaN(size) || size < 50 || size > 1000) {
      showToast('单笔仓位必须在 50 到 1000 USDT 之间', 'error');
      return;
    }

    // 验证 takeProfitPercent
    const tp = Number(takeProfitPercent);
    if (!takeProfitPercent || isNaN(tp) || tp <= 0 || tp > 100) {
      showToast('止盈比例必须在 0% 到 100% 之间', 'error');
      return;
    }

    // 验证 stopLossPercent
    const sl = Number(stopLossPercent);
    if (!stopLossPercent || isNaN(sl) || sl <= 0 || sl > 50) {
      showToast('止损比例必须在 0% 到 50% 之间', 'error');
      return;
    }

    onConfirm({
      enableAutoTrading,
      monitorCount: count,
      minVolume: vol,
      excludedSymbols,
      systemPromptId: selectedSystemPromptId,
      tradingParams: {
        leverage: lev,
        positionSize: size,
        takeProfitPercent: tp / 100,
        stopLossPercent: sl / 100,
      },
      rankingAlgorithm,
      timeFrame,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-gray-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-md w-full mx-4 shadow-2xl relative overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-4">确认启动自主监控</h3>
        <p className="text-white/70 text-sm mb-4">启动后将自动监控波动率排名前N的币种，每小时检查一次。</p>
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">启动数量</label>
            <input
              type="number"
              min="1"
              max="50"
              value={monitorCount}
              onChange={(e) => handleNumberInput(e.target.value, setMonitorCount, 50)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">最低成交量 (USDT)</label>
            <input
              type="number"
              min="0"
              step="100000"
              value={minVolume}
              onChange={(e) => handleNumberInput(e.target.value, setMinVolume)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-white/70 text-sm mb-2">排名算法</label>
          <div className="flex space-x-4">
            <button
              className={`flex-1 py-2 rounded-lg border ${
                rankingAlgorithm === 'volatility'
                  ? 'bg-green-500/20 border-green-500 text-green-500'
                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
              }`}
              onClick={() => setRankingAlgorithm('volatility')}
            >
              波动率
            </button>
            <button
              className={`flex-1 py-2 rounded-lg border ${
                rankingAlgorithm === 'momentum'
                  ? 'bg-green-500/20 border-green-500 text-green-500'
                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
              }`}
              onClick={() => setRankingAlgorithm('momentum')}
            >
              脉冲动量
            </button>
          </div>
        </div>
        <div className="mb-4">
          <SelectDropdown
            label="时间粒度"
            value={timeFrame}
            onChange={(val) => setTimeFrame(val as BarInterval)}
            options={COMMON_TIMEFRAMES}
            placeholder="请选择时间粒度"
          />
        </div>
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">杠杆倍数 (x)</label>
            <input
              type="number"
              min="1"
              max="5"
              value={leverage}
              onChange={(e) => handleNumberInput(e.target.value, setLeverage, 5)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">单笔仓位 (USDT)</label>
            <input
              type="number"
              min="50"
              max="1000"
              step="10"
              value={positionSize}
              onChange={(e) => handleNumberInput(e.target.value, setPositionSize, 1000)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">止盈比例 (%)</label>
            <input
              type="number"
              min="1"
              max="100"
              step="0.5"
              value={takeProfitPercent}
              onChange={(e) => handleNumberInput(e.target.value, setTakeProfitPercent, 100, true)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">止损比例 (%)</label>
            <input
              type="number"
              min="1"
              max="50"
              step="0.5"
              value={stopLossPercent}
              onChange={(e) => handleNumberInput(e.target.value, setStopLossPercent, 50, true)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <MultiSelectDropdown
          label="排除币种"
          value={excludedSymbols}
          onChange={setExcludedSymbols}
          options={availableSymbols}
          loading={loadingSymbols}
          placeholder="点击选择要排除的币种..."
          searchPlaceholder="搜索币种..."
          emptyText="暂无币种数据"
          tagColor="red"
        />

        {/* 预览币种区域 */}
        <div className="mb-4 p-3 bg-black/20 rounded-lg border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 text-xs">预计启动 ({previewSymbols.length})</span>
            {loadingPreview && <div className="animate-spin w-3 h-3 border-b-2 border-white/50 rounded-full"></div>}
          </div>
          {previewSymbols.length > 0 ? (
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto custom-scrollbar">
              {previewSymbols.map((item) => (
                <span
                  key={item.symbol}
                  className="px-2 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded text-xs flex items-center gap-1"
                  title={
                    rankingAlgorithm === 'momentum'
                      ? `脉冲值: ${item.value.toFixed(2)}`
                      : `波动率: ${item.value.toFixed(2)}%`
                  }
                >
                  {item.symbol}
                  <span className="opacity-60 scale-90">
                    {item.value.toFixed(2)}
                    {rankingAlgorithm === 'momentum' ? '' : '%'}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-white/30 text-xs text-center py-2">暂无符合条件的币种</div>
          )}
        </div>

        <SelectDropdown
          label="系统提示词"
          value={selectedSystemPromptId}
          onChange={(val) => setSelectedSystemPromptId(val as string | undefined)}
          options={[
            { value: '', label: '默认提示词' },
            ...systemPrompts.map((prompt) => ({
              value: prompt.promptId || prompt.id.toString(),
              label: prompt.promptId || prompt.title,
            })),
          ]}
          disabled={loadingPrompts}
          loading={loadingPrompts}
          placeholder="默认提示词"
          className="mb-4"
        />
        <label className="flex items-center gap-2 text-white/70 text-sm mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={enableAutoTrading}
            onChange={(e) => setEnableAutoTrading(e.target.checked)}
            className="w-4 h-4 rounded border-white/30 bg-white/10 text-green-500 focus:ring-green-500 focus:ring-offset-0 focus:ring-offset-transparent focus:ring-2"
          />
          <span>启用自动交易</span>
        </label>
        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/50 text-green-300 rounded-lg hover:bg-green-500/30 transition-all"
            disabled={loading}
          >
            {loading ? '启动中...' : '确认启动'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/20 text-white/70 rounded-lg hover:bg-white/20 transition-all"
            disabled={loading}
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoMonitorDialog;
